/**
 * @file: useOpenChatCard.ts
 *
 * Routing OUT of a conversation when a card is tapped — one responsibility:
 * take the card to its destination. Task/report cards deep-link to their page;
 * a contact card opens a chat with that person instead. Returns the open
 * function; the coordinator exposes it as `onOpenCard`. (Opening an external
 * URL and previewing media are separate concerns.)
 */

import { useFloatingChat } from '@euler/app/flows/chat/lib/floating/context';
import { ReportRef } from '@euler/app/flows/report/functions';
import { getGlobalNavigationRef } from '@euler/app/navigation/RoutesContainer';
import { ChatMessagePayload } from '@euler/model/chat';
import { reportContentTypeToReportTabKey } from '@euler/model/util';
import { CommonActions } from '@react-navigation/native';
import { useCallback } from 'react';

export function useOpenChatCard(): (payload: ChatMessagePayload) => void {
  const { openChat } = useFloatingChat();
  return useCallback(
    (payload: ChatMessagePayload) => {
      // A contact card opens (or resumes) a chat with that person — the same
      // app-wide entry every "start chat" button uses, so it resolves the room
      // before opening and never touches a navigator.
      if (payload.type === 'contact_card') {
        openChat({
          kind: 'contact',
          peer: {
            platformUserId: payload.platformUserId,
            appId: payload.appId,
            appScope: payload.appScope,
            name: payload.name,
            avatar: payload.avatar,
          },
        });
        return;
      }
      // Push via the app-root ref, not the nearest navigator: a card tapped in
      // the floating chat panel runs inside that panel's own standalone
      // NavigationContainer, from which `Task`/`ServiceRecord`/`Report` aren't
      // reachable. The root ref dispatches to the app stack from any container.
      const root = getGlobalNavigationRef();
      if (!root?.isReady()) return;
      const go = (name: string, params: object) =>
        root.dispatch(CommonActions.navigate({ name, params }));
      try {
        if (payload.type === 'order_card') {
          if (payload.taskNo) {
            go('Task', { taskNo: payload.taskNo });
          } else {
            console.warn('order card has no taskNo to navigate to');
          }
        } else if (payload.type === 'service_record_card') {
          if (payload.serviceRecordId) {
            go('ServiceRecord', { id: payload.serviceRecordId });
          } else {
            console.warn('service-record card has no id to navigate to');
          }
        } else if (payload.type === 'report_card') {
          const ref: ReportRef = {
            type: 'report',
            id: payload.reportNo,
            orderNo: payload.orderNo,
            reportNo: payload.reportNo,
            licensePlateNo: payload.vehicle?.licensePlateNo ?? '',
            vehicleName: payload.vehicle?.name ?? '',
            vehicleImageUrl: payload.vehicle?.imageUrl,
          };
          go('Report', {
            target: ref,
            initialTab: reportContentTypeToReportTabKey(payload.reportType),
          });
        }
      } catch (e) {
        console.error('card navigation failed', e);
      }
    },
    [openChat],
  );
}
